import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import SectionHead from "@/components/SectionHead";
import { INDUSTRIES, CATEGORIES } from "@/data/industries";
import { waLink } from "@/data/site";
import { useLang } from "@/i18n";
import {
  Disc, CarFront, UtensilsCrossed, BedDouble, Smile, HeartPulse, Scissors, Dumbbell,
  Sofa, Building2, GraduationCap, Scale, Gem, Shirt, Camera, Heart, Armchair, Wrench, Truck, PawPrint,
} from "lucide-react";

const ICONS = {
  tyre: Disc, car: CarFront, restaurant: UtensilsCrossed, hotel: BedDouble, dental: Smile,
  clinic: HeartPulse, salon: Scissors, gym: Dumbbell, interior: Sofa, builder: Building2,
  tuition: GraduationCap, lawyer: Scale, jewellery: Gem, boutique: Shirt, photography: Camera,
  wedding: Heart, furniture: Armchair, home: Wrench, movers: Truck, pet: PawPrint,
};

const Showroom = () => {
  const [filter, setFilter] = useState("all");
  const t = useLang().t;
  const visible = INDUSTRIES.map((ind, idx) => ({ ...ind, idx })).filter((i) => filter === "all" || i.category === filter);

  return (
    <section id="demos" data-testid="showroom" className="bg-[#F5F2EB] py-16 sm:py-24">
      <div className="container-site">
        <SectionHead
          testId="showroom-head"
          eyebrow={t.showroom.eyebrow}
          title={t.showroom.title}
          sub={t.showroom.sub}
        />

        <div className="mt-10 flex flex-wrap justify-center gap-2.5" data-testid="showroom-filters">
          {CATEGORIES.map((c, i) => (
            <button
              key={c.key}
              data-testid={`filter-${c.key}`}
              onClick={() => setFilter(c.key)}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                filter === c.key
                  ? "border-[#4A362E] bg-[#4A362E] text-white"
                  : "border-[#D7B594] text-[#5D4037] hover:bg-[#FDFBF7]"
              }`}
            >
              {t.showroom.filters[i]}
            </button>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="showroom-grid">
          {visible.map((ind) => {
            const Icon = ICONS[ind.icon] || Building2;
            return (
              <div key={ind.slug} data-testid={`industry-card-${ind.slug}`} className="card-site group overflow-hidden">
                {ind.demo ? (
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={ind.image}
                      alt={`${t.industries[ind.idx].name} website demo`}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 rounded-full bg-[#3E2723]/85 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                      {t.showroom.live}
                    </span>
                  </div>
                ) : (
                  <div className="flex h-24 items-center justify-between bg-[#FDFBF7] px-6">
                    <Icon className="h-10 w-10 text-[#A88667]" />
                    <span className="rounded-full border border-[#D7B594] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#8D6E63]">
                      {t.showroom.onRequest}
                    </span>
                  </div>
                )}
                <div className="p-6">
                  <h3 className="font-heading text-lg font-bold text-[#3E2723]">{t.industries[ind.idx].name}</h3>
                  <p className="mt-1.5 text-sm text-[#5D4037]">{t.industries[ind.idx].blurb}</p>
                  {ind.demo ? (
                    <Link
                      to={`/demos/${ind.slug}`}
                      data-testid={`demo-link-${ind.slug}`}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#4A362E] transition-colors duration-200 hover:text-[#A88667]"
                    >
                      {t.showroom.explore} <ArrowRight className="h-4 w-4" />
                    </Link>
                  ) : (
                    <a
                      href={waLink(`Hi Anjum, I run a ${ind.name.toLowerCase()} business and would like to see a website demo for my industry.`)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`demo-request-${ind.slug}`}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#25D366] transition-colors duration-200 hover:text-[#1EB856]"
                    >
                      <MessageCircle className="h-4 w-4" /> {t.showroom.request}
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-10 text-center text-sm text-[#8D6E63]">
          {t.showroom.otherPre} <a href={waLink("Hi Anjum, my business type is not listed on your website. Can you build a website for my business?")} target="_blank" rel="noopener noreferrer" data-testid="showroom-other-industry" className="font-semibold text-[#4A362E] underline underline-offset-4">{t.showroom.otherLink}</a> {t.showroom.otherPost}
        </p>
      </div>
    </section>
  );
};

export default Showroom;
